// Briefing: today's seeded route, the forecast the predicted range will cover, cruise controls in real units,
// and "Start run" (unlocks audio inside the tap, loads the engine recording with a progress bar).
import type { AppContext, ScreenHandle } from './app';
import type { Controls, Mixture, Route } from '../types';
import { allowedMixtures, sanitizeControls, isJet } from '../physics/resolve';
import { predictRange } from '../physics/predict';
import { generateRoute, todaySeed } from '../sim/route';
import { EngineVoice } from '../audio/engineVoice';
import { AUDIO_UNAVAILABLE } from '../audio/manifest';
import { h, clear, button, cell } from './dom';
import { fmtNm, fmtHours, fmtFt, fmtRpm, fmtSeedDate, fmtPct } from './format';
import { craftBuild, fmtEfficiency, type CraftBuild } from './build';
import { forecastRows, signed, AIR_LAYER_LABELS } from './forecast';

const MIXTURE_LABEL: Record<Mixture, string> = { rich: 'Rich', peak: 'Peak EGT', lop: 'Lean of peak' };

export function mountBriefing(root: HTMLElement, ctx: AppContext): ScreenHandle {
  let b: CraftBuild | null = null;
  let route: Route | null = null;
  let controls: Controls = {};
  let starting = false;

  const title = h('h1', { id: 'briefing-title' }, 'Briefing');
  const sub = h('p');
  const form = h('div.panel', { role: 'group', 'aria-label': 'Cruise settings' });
  const readoutCells = h('div.cells');
  const forecast = h('div.panel');
  const progress = h('progress', { max: '1', value: '0', 'aria-label': 'Loading engine recording' });
  const loadText = h('p.small.muted', { role: 'status' });
  const loadBar = h('div.load-bar', { hidden: true }, progress, loadText);
  const start = button('Start run', () => void startRun(), { cls: 'btn btn-primary' });
  root.append(
    h('div.wrap', {},
      h('header.topbar', {},
        button('← Workshop', () => { if (ctx.session.craftId) ctx.go('workshop', { craftId: ctx.session.craftId }); }, { cls: 'btn btn-quiet', ariaLabel: 'Back to the workshop' }),
        h('div.title', {}, title, sub),
      ),
      h('div.two-col', {}, h('div', {}, form, h('div.readout', { 'aria-live': 'polite', style: 'position:static' }, readoutCells, start), loadBar), forecast),
    ),
  );

  function set(patch: Partial<Controls>): void {
    if (!b) return;
    controls = sanitizeControls(b.rc, { ...controls, ...patch });
    buildForm();
    readout();
  }

  function stepper(label: string, value: number, min: number, max: number, step: number, fmt: (v: number) => string, onChange: (v: number) => void, hint?: string): HTMLElement {
    const id = `ctl-${label.replace(/\W+/g, '-').toLowerCase()}`;
    const out = h('span.cell-value', { id: `${id}-value` }, fmt(value));
    const range = h('input', { type: 'range', id, min: String(min), max: String(max), step: String(step), value: String(value), 'aria-valuetext': fmt(value) });
    range.addEventListener('input', () => { out.textContent = fmt(Number(range.value)); });
    range.addEventListener('change', () => onChange(Number(range.value)));
    const dec = button('−', () => onChange(Math.max(min, value - step)), { cls: 'btn', ariaLabel: `${label}: decrease` });
    const inc = button('+', () => onChange(Math.min(max, value + step)), { cls: 'btn', ariaLabel: `${label}: increase` });
    return h('div.field', {}, h('label', { for: id }, `${label}: `, out), h('div.stepper', {}, dec, range, inc), hint ? h('p.hint', {}, hint) : null);
  }

  function segmented(label: string, options: { value: string; label: string; disabled?: boolean; reason?: string }[], current: string, onPick: (v: string) => void): HTMLElement {
    const seg = h('div.seg', { role: 'group', 'aria-label': label });
    for (const o of options) {
      const btn = button(o.label, () => onPick(o.value), { cls: 'btn', disabled: o.disabled, ariaLabel: o.reason ? `${o.label}: ${o.reason}` : undefined });
      btn.setAttribute('aria-pressed', String(o.value === current));
      if (o.reason) btn.title = o.reason;
      seg.append(btn);
    }
    return h('div.field', {}, h('span.label', {}, label), seg);
  }

  function buildForm(): void {
    if (!b) return;
    clear(form);
    form.append(h('h2', {}, 'Cruise plan'));
    const { rc, engine } = b;
    if (rc.air) {
      const ceilingFt = Math.floor(rc.air.ceilingM / 0.3048 / 1000) * 1000;
      form.append(stepper('Cruise altitude', controls.altitudeFt ?? 0, 0, ceilingFt, 1000, fmtFt, (v) => set({ altitudeFt: v }), `Service ceiling ${fmtFt(rc.air.ceilingM / 0.3048)}`));
      if (isJet(rc)) {
        form.append(stepper('Thrust', Math.round((controls.thrust ?? 0.6) * 100), 60, 100, 5, (v) => `${v} % of available`, (v) => set({ thrust: v / 100 })));
        if (rc.air.afterburnerThrustN) {
          const ab = h('input', { type: 'checkbox', id: 'ctl-ab' });
          ab.checked = !!controls.afterburner;
          ab.addEventListener('change', () => set({ afterburner: ab.checked }));
          form.append(h('div.field', {}, h('label.switch', { for: 'ctl-ab' }, ab, 'Afterburner (afterburner TSFC applies)')));
        }
      } else {
        const power = controls.power ?? 0.65;
        form.append(segmented('Power', [0.55, 0.65, 0.75].map((p) => ({ value: String(p), label: fmtPct(p) })), String(power), (v) => set({ power: Number(v) })));
        const allowed = allowedMixtures(rc, power);
        const reason = (m: Mixture): string | undefined => {
          if (allowed.includes(m)) return undefined;
          if (m === 'lop') return rc.air?.fuelInjected ? 'needs the engine monitor mod' : 'needs fuel injection';
          return 'only at 75 % power or less';
        };
        form.append(segmented('Mixture', (['rich', 'peak', 'lop'] as Mixture[]).map((m) => ({ value: m, label: MIXTURE_LABEL[m], disabled: !allowed.includes(m), reason: reason(m) })), controls.mixture ?? 'rich', (v) => set({ mixture: v as Mixture })));
        if (rc.air.propeller === 'constant-speed') {
          const lo = Math.round((engine.ratedRpm * 0.6) / 50) * 50;
          form.append(stepper('Propeller rpm', controls.propRpm ?? engine.ratedRpm, lo, engine.ratedRpm, 50, fmtRpm, (v) => set({ propRpm: v })));
        }
      }
    }
    if (rc.sea) {
      form.append(stepper('Speed through the water', controls.speedKn ?? rc.sea.designSpeedKn, rc.sea.minSpeedKn, rc.sea.maxSpeedKn, 0.5, (v) => `${v.toFixed(1)} kn`, (v) => set({ speedKn: v }), `Hull design speed ${rc.sea.designSpeedKn} kn`));
    }
  }

  function readout(): void {
    if (!b || !route) return;
    clear(readoutCells);
    const r = predictRange(b.rc, controls);
    readoutCells.append(
      cell(b.craft.domain === 'air' ? 'Predicted range, still air' : 'Predicted range, calm sea', fmtNm(r.rangeNm)).root,
      cell('Endurance', fmtHours(r.hours)).root,
      cell('Efficiency', fmtEfficiency(r.efficiency, b.craft.domain, b.big)).root,
    );
    forecastTable(route, r.rangeNm);
  }

  function forecastTable(rt: Route, rangeNm: number): void {
    clear(forecast);
    const { rows, truncated } = forecastRows(rt, rangeNm);
    const air = rt.domain === 'air';
    forecast.append(h('h2', {}, 'Forecast along the track'), h('p.muted.small', {}, air
      ? 'Along-track wind by altitude layer (kt): tailwind positive, headwind negative. The forecast is the actual weather.'
      : 'Along-track current (kn, favourable positive) and Douglas sea state. The forecast is the actual weather.'));
    if (!rows.length) { forecast.append(h('p.state', {}, 'No forecast segments.')); return; }
    const head = h('tr', {}, h('th', {}, 'Segment'));
    if (air) for (const l of AIR_LAYER_LABELS) head.append(h('th', {}, l));
    else head.append(h('th', {}, 'Current'), h('th', {}, 'Sea state'));
    head.append(h('th', {}, 'Weather'));
    const tbody = h('tbody');
    for (const r of rows) {
      const tr = h('tr', { class: r.weather ? 'wx' : undefined }, h('td', {}, `${r.no} · ${r.fromNm}–${r.toNm} nm`));
      if (r.windKt) for (const w of r.windKt) tr.append(h('td', {}, signed(w)));
      else tr.append(h('td', {}, `${signed(r.currentKn ?? 0, 1)} kn`), h('td', {}, String(r.seaState ?? 0)));
      tr.append(h('td', {}, r.weather ? 'Cell' : '—'));
      tbody.append(tr);
    }
    forecast.append(h('div.table-wrap', {}, h('table.table', {}, h('thead', {}, head), tbody)));
    if (truncated) forecast.append(h('p.small.muted', { style: 'margin-top:.5rem' }, `Showing the first ${rows.length} of ${rt.segments.length} segments; the rest appear during the run.`));
  }

  async function startRun(): Promise<void> {
    if (!b || starting) return;
    ctx.audio.unlock();
    starting = true;
    start.disabled = true;
    let notice: string | null = null;
    const { engine } = b;
    if (!ctx.save.settings.audio) {
      notice = 'Engine audio is off in Settings';
    } else if (!engine.audioBank) {
      notice = AUDIO_UNAVAILABLE;
    } else {
      loadBar.hidden = false;
      progress.value = 0;
      loadText.textContent = `Loading the ${engine.name} recording…`;
      try {
        const voice = await EngineVoice.load(engine.audioBank, (p) => { progress.value = p; });
        ctx.audio.voice?.dispose();
        ctx.audio.voice = voice;
        voice.setVolume(ctx.save.settings.volume);
        loadText.textContent = 'Recording ready.';
      } catch (e) {
        notice = AUDIO_UNAVAILABLE;
        loadText.textContent = e instanceof Error ? e.message : AUDIO_UNAVAILABLE;
      }
    }
    starting = false;
    start.disabled = false;
    loadBar.hidden = true;
    ctx.go('run', { craftId: b.craft.id, controls, audioNotice: notice });
  }

  function render(): void {
    const id = ctx.session.craftId;
    b = id ? craftBuild(ctx.save, id) : null;
    clear(form); clear(readoutCells); clear(forecast);
    loadBar.hidden = true;
    if (!b) {
      sub.textContent = '';
      form.append(h('p.state', {}, 'No craft selected. Go back to the hangar and pick one.'));
      start.disabled = true;
      return;
    }
    start.disabled = false;
    const seed = todaySeed();
    route = generateRoute(seed, b.craft.domain);
    title.textContent = `Briefing · ${b.craft.className}`;
    sub.textContent = `Route seed ${fmtSeedDate(seed)} (UTC) · ${fmtNm(route.lengthNm)} of track · ${b.mods.length ? `${b.mods.length} mod${b.mods.length > 1 ? 's' : ''} installed` : 'stock build'}`;
    const keep = ctx.session.controls && ctx.session.craftId === b.craft.id ? ctx.session.controls : b.craft.defaultCruise;
    controls = sanitizeControls(b.rc, { ...keep });
    buildForm();
    readout();
  }

  return { show: render };
}
